import { EnumUserRoles } from 'entities/Users/model/types'

/**
 * Per-role system-prompt fragment, appended to the base prompt so the model guides
 * for what the role can do and explains-then-escalates for what it cannot. Owner
 * and Admin share the full-access fragment. These fragments only tell the model;
 * navigation is gated for real in entities/WebMcp/model/roleGating, which must stay
 * in step with them.
 */
const ADMIN_FRAGMENT =
  'User role: admin, with full access. You may navigate to any screen and guide the user through every area, including billing, compliance registration, member management, and number provisioning.'

const MANAGER_FRAGMENT =
  "User role: manager. You may guide and navigate inboxes, contacts, messaging, broadcasts, campaigns, org settings, and integrations. You must NOT navigate to billing, compliance registration, or member management. For those, explain the feature and tell the user it requires owner or admin access. Analytics is available but scoped to the user's own team."

const MEMBER_FRAGMENT =
  'User role: member. You may guide and navigate inboxes, contacts (which may be scoped to what this member can access), messaging, broadcasts, and conversations. You must NOT navigate to analytics, billing, compliance, member management, contact import, or number provisioning. Explain the feature and say it requires admin or manager access.'

const OBSERVER_FRAGMENT =
  'User role: observer (view only), READ ONLY. You may surface contact and conversation data and explain features, but you must not take, or navigate to, any write or settings action. For any such request, tell the user their account is read-only and to contact their admin to change their role.'

const ROLE_FRAGMENTS: Record<EnumUserRoles, string> = {
  [EnumUserRoles.OWNER]: ADMIN_FRAGMENT,
  [EnumUserRoles.ADMIN]: ADMIN_FRAGMENT,
  [EnumUserRoles.MANAGER]: MANAGER_FRAGMENT,
  [EnumUserRoles.MEMBER]: MEMBER_FRAGMENT,
  [EnumUserRoles.VIEW_ONLY]: OBSERVER_FRAGMENT,
}

export const roleFragment = (role: EnumUserRoles): string => ROLE_FRAGMENTS[role]

// One role-aware capability sentence for the agent panel's description line (US4).
// Generic by design: it names no specific feature, so it stays accurate as the
// WebMCP tool set grows. Acting roles share one line; view-only gets its own,
// framed around viewing so a read-only user is not promised actions.
const ACTING_LINE =
  "I'm your Salesmsg assistant. I can help you get set up, find your way around the app, and work through tasks step by step."

const OBSERVER_LINE =
  "I'm your Salesmsg assistant. I can show you around the app and answer questions about what you're viewing."

const CAPABILITY_LINES: Record<EnumUserRoles, string> = {
  [EnumUserRoles.OWNER]: ACTING_LINE,
  [EnumUserRoles.ADMIN]: ACTING_LINE,
  [EnumUserRoles.MANAGER]: ACTING_LINE,
  [EnumUserRoles.MEMBER]: ACTING_LINE,
  [EnumUserRoles.VIEW_ONLY]: OBSERVER_LINE,
}

const DEFAULT_LINE =
  "I'm your Salesmsg assistant. I can help you get set up, find your way around the app, and answer questions."

export const capabilityLine = (role?: EnumUserRoles): string =>
  role ? CAPABILITY_LINES[role] : DEFAULT_LINE
